import { getAuthClient } from "#auth-client";
import type { AuthSessionSnapshot } from "#auth-session-snapshot";

/**
 * Web: the session is NOT available synchronously, so this hook reports the real pending window.
 *
 * `expoClient` primes its session atom from cache only when `Platform.OS !== "web"` (the block is
 * `!isWeb`-gated in `getActions`), and browser cookie auth has no synchronous equivalent. The
 * first render therefore happens while `get-session` is still in flight: `isResolved` is `false`
 * and `user` is `null` meaning "unknown", flipping to a resolved snapshot when the response lands.
 *
 * Consumers that must not act on a guess should branch on `isResolved`; consumers that only
 * render an entry point can treat an unresolved read as signed-out, which is the convention the
 * rest of the app's session-derived chrome already follows.
 */
export function useAuthSession(): AuthSessionSnapshot {
  const { data: session, isPending } = getAuthClient().useSession();

  if (isPending) {
    return { isResolved: false, user: null };
  }

  return { isResolved: true, user: session?.user ?? null };
}
